import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import {
  RichTextEditor,
  type PickedImage,
  type RichTextEditorProps,
} from '@nucleus-commerce/admin-kit/forms';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';

import { mediaQueryOptions } from '../hooks/use-media';

import { CompactMediaPicker } from './compact-media-picker';

export type MediaRichTextEditorProps = Omit<RichTextEditorProps, 'onPickImage'>;

/** The admin-kit HTML editor with its image button wired to the organisation's media library. */
export function MediaRichTextEditor(props: MediaRichTextEditorProps) {
  const { t } = useTranslation('media');
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const pending = useRef<((image: PickedImage | null) => void) | null>(null);

  const finish = (image: PickedImage | null) => {
    pending.current?.(image);
    pending.current = null;
    setOpen(false);
  };

  const pickImage = () =>
    new Promise<PickedImage | null>((resolve) => {
      pending.current = resolve;
      setError(null);
      setOpen(true);
    });

  const choose = async (mediaId: string) => {
    try {
      const media = await queryClient.query(mediaQueryOptions(mediaId));
      finish({ src: media.renditions?.large ?? media.url, alt: media.alt ?? null });
    } catch (cause) {
      setError(cause);
    }
  };

  return (
    <>
      <RichTextEditor {...props} onPickImage={pickImage} />
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) finish(null);
        }}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('richText.dialogTitle')}</DialogTitle>
            <DialogDescription>{t('richText.dialogDescription')}</DialogDescription>
          </DialogHeader>
          {error ? <ErrorState error={error} onRetry={() => setError(null)} /> : null}
          <CompactMediaPicker
            value={null}
            initialKind="image"
            kindLocked
            onChange={(mediaId) => void choose(mediaId)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
